'use client';

import { useState, useEffect, Suspense } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { motion } from 'framer-motion';
import { AppShell } from '../../src/components/AppShell';
import { CompatibilitySection } from '../../src/components/CompatibilitySection';
import { ConnectionInventoryPanel, ConnectionsUnifiedEmpty } from '../../src/components/community/ConnectionInventoryPanel';
import { DiscoveryUserSearch } from '../../src/components/community/DiscoveryUserSearch';
import { MessagesTab } from '@/components/community/messages/MessagesTab';
import { MessageThread } from '@/components/community/messages/MessageThread';
import { useProfile } from '../../src/core/social/hooks';
import { hasRealChart } from '../../src/core/social/constants';
import { useHydrateCompositionUrls } from '../../src/hooks/useHydrateCompositionUrls';
import type { RelationalIntent } from '../../src/lib/relational-intent';
import { Button } from '@/components/shared/Button';
import { Card } from '@/components/shared/Card';
import { Tabs } from '@/components/shared/Tabs';
import { FtueConnectionsWelcomeBanner } from '@/components/ftue/FtueConnectionsWelcomeBanner';
import { isFeatureEnabled } from '@/core/config/flags';
import { CommunityFeed } from '@/components/community/posts/CommunityFeed';
import { CommunityNotificationsPanel } from '@/components/community/posts/CommunityNotificationsPanel';
import {
  ConnectionsTabIcon,
  DiscoveryTabIcon,
  FeedTabIcon,
  MessagesTabIcon,
} from '@/components/community/posts/community-post-icons';

const GROUPS_INTRO = 'Private groups of your connections used to view relational activation.';

type CommunityTabId = 'discovery' | 'connections' | 'messages' | 'feed';

function GroupsList({
  userId,
  onMetaChange,
}: {
  userId: string | null;
  onMetaChange?: (meta: { loading: boolean; empty: boolean }) => void;
}) {
  const [groups, setGroups] = useState<Array<{ id: string; slug: string; name: string; description: string }>>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDesc, setCreateDesc] = useState('');
  const [createSlug, setCreateSlug] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!userId) {
      setGroups([]);
      setLoading(false);
      onMetaChange?.({ loading: false, empty: true });
      return;
    }
    setLoading(true);
    fetch('/api/groups', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => {
        const next = d.groups || [];
        setGroups(next);
        setLoading(false);
        onMetaChange?.({ loading: false, empty: next.length === 0 });
      })
      .catch(() => {
        setGroups([]);
        setLoading(false);
        onMetaChange?.({ loading: false, empty: true });
      });
  }, [userId, onMetaChange]);

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createName.trim() || !userId) return;
    setCreating(true);
    try {
      const r = await fetch('/api/groups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          name: createName.trim(),
          description: createDesc.trim(),
          slug: createSlug.trim() || undefined,
        }),
      });
      if (r.ok) {
        const g = await r.json();
        setCreateOpen(false);
        setCreateName('');
        setCreateDesc('');
        setCreateSlug('');
        setGroups((prev) => [g, ...prev]);
      }
    } finally {
      setCreating(false);
    }
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? groups.filter(
        (g) =>
          g.name.toLowerCase().includes(q) ||
          (g.slug || '').toLowerCase().includes(q) ||
          (g.description || '').toLowerCase().includes(q)
      )
    : groups;

  if (!userId) {
    return (
      <div className="max-w-4xl mx-auto space-y-8">
        <Card elevation="raised" padding="px-4 py-3" className="text-sm text-text-secondary">
          {GROUPS_INTRO}
        </Card>
        <p className="text-sm text-text-secondary">Sign in to list and create relational chart groups.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <Card elevation="raised" padding="px-4 py-3" className="text-sm text-text-secondary">
        {GROUPS_INTRO}
      </Card>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          placeholder="Filter by name or slug…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="input flex-1 min-w-[200px]"
        />
        <Button type="button" variant="primary" size="sm" onClick={() => setCreateOpen(true)}>
          Create group
        </Button>
      </div>
      {createOpen && (
        <form onSubmit={onCreate} className="rounded-lg border border-border bg-surface-1 p-4 space-y-3">
          <input
            placeholder="Group name"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            className="input w-full"
            required
          />
          <input
            placeholder="Slug (optional)"
            value={createSlug}
            onChange={(e) => setCreateSlug(e.target.value)}
            className="input w-full"
          />
          <textarea
            placeholder="Description"
            value={createDesc}
            onChange={(e) => setCreateDesc(e.target.value)}
            className="input w-full"
            rows={2}
          />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={creating}
              className="px-4 py-2 rounded-lg bg-accent text-white text-sm font-medium disabled:opacity-50"
            >
              Create
            </button>
            <button
              type="button"
              onClick={() => setCreateOpen(false)}
              className="px-4 py-2 rounded-lg border border-border text-sm"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
      {loading ? (
        <p className="text-text-secondary text-sm">Loading groups…</p>
      ) : filtered.length === 0 ? (
        <Card elevation="raised" size="lg" className="text-center">
          <p className="text-text-secondary text-sm">No groups match.</p>
          <p className="text-xs text-text-secondary mt-1">{GROUPS_INTRO}</p>
        </Card>
      ) : (
        <ul className="space-y-3">
          {filtered.map((g) => (
            <li key={g.id}>
              <Link
                href={`/community/group/${encodeURIComponent(g.slug || g.id)}`}
                className="block rounded-lg border border-border bg-surface-1 p-4 hover:bg-surface-2"
              >
                <h3 className="font-medium text-text-primary">{g.name}</h3>
                <p className="text-sm text-text-secondary mt-1 line-clamp-2">{g.description}</p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CommunityClientInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [activeTab, setActiveTab] = useState<CommunityTabId>('discovery');
  const [discoveryIntent, setDiscoveryIntent] = useState<RelationalIntent>('friend');
  const [inventoryRefreshSignal, setInventoryRefreshSignal] = useState(0);
  const bumpCommunityInventory = () => setInventoryRefreshSignal((n) => n + 1);
  const [dmUnreadTotal, setDmUnreadTotal] = useState(0);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [dmRefreshSignal, setDmRefreshSignal] = useState(0);
  const [inventoryMeta, setInventoryMeta] = useState({ loading: true, empty: true });
  const [groupsMeta, setGroupsMeta] = useState({ loading: true, empty: true });
  const { user, primaryChart, loading: profileLoading } = useProfile();

  useHydrateCompositionUrls();

  useEffect(() => {
    const t = searchParams.get('tab');
    const dmOn = isFeatureEnabled('ENABLE_DIRECT_MESSAGES');
    if (t === 'discovery' || t === 'connections' || t === 'messages' || t === 'feed') {
      if (t === 'messages' && !dmOn) {
        setActiveTab('discovery');
      } else {
        setActiveTab(t);
      }
    } else if (!t) {
      setActiveTab('discovery');
    }
    const conv = searchParams.get('conversation');
    if (conv && conv.trim() && dmOn) {
      setActiveTab('messages');
      setSelectedConversationId(conv.trim());
    }
  }, [searchParams]);

  const setTab = (t: CommunityTabId) => {
    setActiveTab(t);
    if (t !== 'messages') {
      setSelectedConversationId(null);
    }
    const next = new URLSearchParams(searchParams.toString());
    next.set('tab', t);
    router.replace(`/community?${next.toString()}`, { scroll: false });
  };

  const dmEnabled = isFeatureEnabled('ENABLE_DIRECT_MESSAGES');

  const tabs: { id: CommunityTabId; label: string; icon: React.ReactNode }[] = [
    { id: 'discovery', label: 'Discovery', icon: <DiscoveryTabIcon /> },
    { id: 'connections', label: 'Connections', icon: <ConnectionsTabIcon /> },
    ...(dmEnabled
      ? [
          {
            id: 'messages' as const,
            label: 'Messages',
            icon: (
              <span className="relative inline-flex">
                <MessagesTabIcon />
                {dmUnreadTotal > 0 ? (
                  <span
                    className="absolute -top-0.5 -right-1 min-w-[0.5rem] h-2 px-0.5 rounded-full bg-accent"
                    aria-label={`${dmUnreadTotal} unread`}
                  />
                ) : null}
              </span>
            ),
          },
        ]
      : []),
    { id: 'feed', label: 'Feed', icon: <FeedTabIcon /> },
  ];

  const showDiscoveryConnectionsChrome = activeTab === 'discovery' || activeTab === 'connections';
  const showListenCta = activeTab === 'connections';

  const seekerChartId = hasRealChart(primaryChart) ? primaryChart!.id : null;

  const showConnectionsUnifiedEmpty =
    Boolean(user?.id) &&
    !inventoryMeta.loading &&
    inventoryMeta.empty &&
    !groupsMeta.loading &&
    groupsMeta.empty;

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto w-full min-w-0 space-y-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center space-y-4 px-4 min-w-0"
        >
          <h1 className="text-h1 font-serif font-bold text-text-primary">Community</h1>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto break-words">
            Discover, connect, and share with the Astradio community.
          </p>
        </motion.div>

        {showListenCta ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <Card size="lg" className="max-w-2xl mx-auto border-accent/40 space-y-4 text-center">
              <h2 className="font-serif text-h3 font-semibold text-text-primary">
                Hear what a relationship sounds like
              </h2>
              <p className="text-body-sm text-text-secondary">
                Combine two charts and hear the sonic signature of a connection.
              </p>
              <Link href="/listen">
                <Button type="button" variant="audio" size="md" className="min-h-[44px]">
                  Choose two charts
                </Button>
              </Link>
            </Card>
          </motion.div>
        ) : null}

        {showDiscoveryConnectionsChrome ? <FtueConnectionsWelcomeBanner /> : null}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="min-w-0 w-full"
        >
          <div className="-mx-2 px-2 min-w-0 overflow-x-auto scrollbar-hide md:mx-0 md:px-0">
            <Tabs
              variant="pill"
              ariaLabel="Community sections"
              tabs={tabs.map((tab) => ({
                id: tab.id,
                label: tab.label,
                icon: tab.icon,
              }))}
              activeTab={activeTab}
              onTabChange={(id) => setTab(id as CommunityTabId)}
            />
          </div>
        </motion.div>

        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="w-full"
        >
          {activeTab === 'discovery' && (
            <div className="max-w-4xl mx-auto space-y-8">
              <div>
                <h2 className="font-serif text-xl font-semibold text-text-primary mb-2">Discovery</h2>
                <p className="text-text-secondary text-sm mb-6">
                  Find meaningful connections based on astrological compatibility. Choose your intent and
                  we&apos;ll show you the best matches.
                </p>
              </div>

              <DiscoveryUserSearch
                currentUserId={user?.id ?? null}
                seekerChartId={seekerChartId}
                intent={discoveryIntent}
                onConnectionRequested={bumpCommunityInventory}
                inventoryRefreshSignal={inventoryRefreshSignal}
              />

              <section className="space-y-3">
                <h2 className="font-serif text-lg font-semibold text-text-primary mb-1">Intentional matching</h2>
                <p className="text-sm text-text-secondary max-w-2xl mb-2">
                  Choose an intent, then find matches. Results load only after you click Find matches and appear
                  directly below.
                </p>
                <CompatibilitySection
                  hasProfile={user !== null}
                  chartId={seekerChartId}
                  limit={10}
                  mode={discoveryIntent}
                  onModeChange={setDiscoveryIntent}
                  onSwitchToProfile={() => router.push('/profile')}
                  currentUserId={user?.id ?? null}
                  onConnectionRequested={bumpCommunityInventory}
                  inventoryRefreshSignal={inventoryRefreshSignal}
                />
              </section>
            </div>
          )}

          {activeTab === 'connections' && (
            <div className="max-w-4xl mx-auto space-y-8">
              {showConnectionsUnifiedEmpty ? (
                <ConnectionsUnifiedEmpty onSwitchToDiscovery={() => setTab('discovery')} />
              ) : null}
              <div className={showConnectionsUnifiedEmpty ? 'hidden' : 'space-y-8'} aria-hidden={showConnectionsUnifiedEmpty}>
                <ConnectionInventoryPanel
                  currentUserId={user?.id ?? null}
                  viewerChartId={seekerChartId}
                  refreshSignal={inventoryRefreshSignal}
                  onMetaChange={setInventoryMeta}
                />
                <div>
                  <h2 className="font-serif text-lg font-semibold text-text-primary mb-2">Relational groups</h2>
                  <GroupsList userId={user?.id ?? null} onMetaChange={setGroupsMeta} />
                </div>
              </div>
            </div>
          )}

          {dmEnabled && activeTab === 'messages' && (
            <div className="max-w-4xl mx-auto">
              {selectedConversationId ? (
                <MessageThread
                  conversationId={selectedConversationId}
                  currentUserId={user?.id ?? null}
                  onBack={() => setSelectedConversationId(null)}
                  onRefreshList={() => setDmRefreshSignal((n) => n + 1)}
                />
              ) : (
                <MessagesTab
                  currentUserId={user?.id ?? null}
                  selectedConversationId={selectedConversationId}
                  onOpenConversation={(id) => setSelectedConversationId(id)}
                  onMetaChange={({ unreadTotal }) => setDmUnreadTotal(unreadTotal)}
                  refreshSignal={dmRefreshSignal}
                  onSwitchToConnections={() => setTab('connections')}
                />
              )}
            </div>
          )}

          {activeTab === 'feed' && (
            <div className="max-w-4xl mx-auto space-y-8">
              <CommunityNotificationsPanel />
              <CommunityFeed />
            </div>
          )}
        </motion.div>
      </div>
    </AppShell>
  );
}

export default function CommunityClient() {
  return (
    <Suspense fallback={<div className="min-h-[40vh] flex items-center justify-center text-text-secondary">Loading Community…</div>}>
      <CommunityClientInner />
    </Suspense>
  );
}
